'use client';

import { UserDto } from '@fms-run/backend-api-lib';
import {
  User,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  createUserWithEmailAndPassword,
} from 'firebase/auth';
import { useRouter } from 'next/navigation';
import React, { createContext, useContext, useEffect, useState } from 'react';

import { useCurrentUser } from '../hooks/queries/useCurrentUser';
import { auth } from '../lib/firebase';

interface AuthContextType {
  firebaseUser: User | null;
  user: UserDto | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [firebaseLoading, setFirebaseLoading] = useState(true);
  const router = useRouter();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setFirebaseUser(user);
      setFirebaseLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Use React Query to fetch user data from backend when Firebase user is authenticated
  const {
    data: backendUser,
    isLoading: userLoading,
    refetch: refetchUser,
  } = useCurrentUser(!!firebaseUser);

  const loading = firebaseLoading || (!!firebaseUser && userLoading);

  useEffect(() => {
    if (!loading && firebaseUser && backendUser) {
      const currentPath = window.location.pathname;

      if (currentPath === '/') {
        router.push('/dashboard');
      }
    }
  }, [loading, firebaseUser, backendUser, router]);

  const login = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
    await refetchUser();
    router.push('/dashboard');
  };

  const loginWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    provider.addScope('email');
    provider.addScope('profile');

    await signInWithPopup(auth, provider);
    await refetchUser();
    router.push('/dashboard');
  };

  const register = async (email: string, password: string) => {
    await createUserWithEmailAndPassword(auth, email, password);
    await refetchUser();
    router.push('/dashboard');
  };

  const logout = async () => {
    await signOut(auth);
    router.push('/');
  };

  const value = {
    firebaseUser,
    loading,
    login,
    loginWithGoogle,
    register,
    logout,
    user: backendUser || null,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  // Handle SSR/build time calls gracefully
  if (context === undefined) {
    if (typeof window === 'undefined') {
      // During SSR/build, return safe defaults
      return {
        firebaseUser: null,
        loading: true,
        login: async () => {},
        loginWithGoogle: async () => {},
        register: async () => {},
        logout: async () => {},
        user: null,
      };
    }
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
